import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { GoogleAuthProvider, getAuth, signInWithPopup } from 'firebase/auth'

import { firebaseApp } from '../firebase'

/**
 * "Continue with Google" button. Opens Firebase's popup-based OAuth flow.
 * Auto-signup is handled server-side via AccountService.Me on first request.
 */
export function GoogleButton({ onError }: { onError?: (msg: string) => void }) {
	const navigate = useNavigate()
	const [loading, setLoading] = useState(false)

	async function handle() {
		setLoading(true)
		try {
			const auth = getAuth(firebaseApp)
			const provider = new GoogleAuthProvider()
			provider.setCustomParameters({ prompt: 'select_account' })
			await signInWithPopup(auth, provider)
			navigate('/')
		} catch (err) {
			const code = (err as { code?: string })?.code
			if (code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request') {
				// User dismissed the popup — not really an error.
			} else {
				onError?.(err instanceof Error ? err.message : 'Google sign-in failed')
			}
		} finally {
			setLoading(false)
		}
	}

	return (
		<button
			type="button"
			onClick={() => void handle()}
			disabled={loading}
			className="w-full flex items-center justify-center gap-3 h-11 px-5 rounded-nayda bg-paper text-ink border border-line-strong hover:bg-bone hover:border-ink transition-colors disabled:opacity-50 font-medium text-[14px]"
		>
			<GoogleGlyph />
			<span>{loading ? 'Connecting…' : 'Continue with Google'}</span>
		</button>
	)
}

function GoogleGlyph() {
	// Multicolor Google "G" — the only place we break the Nayda monochrome rule,
	// because Google's brand guidelines require the colored mark on sign-in buttons.
	return (
		<svg
			width="18"
			height="18"
			viewBox="0 0 18 18"
			aria-hidden
		>
			<path
				d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.79 2.71v2.26h2.9c1.7-1.56 2.69-3.87 2.69-6.61z"
				fill="#4285F4"
			/>
			<path
				d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.9-2.26c-.8.54-1.83.86-3.06.86-2.35 0-4.34-1.59-5.05-3.72H.95v2.34A9 9 0 0 0 9 18z"
				fill="#34A853"
			/>
			<path
				d="M3.95 10.7A5.4 5.4 0 0 1 3.65 9c0-.59.1-1.16.28-1.7V4.96H.95A9 9 0 0 0 0 9c0 1.45.35 2.83.95 4.04l3-2.34z"
				fill="#FBBC05"
			/>
			<path
				d="M9 3.58c1.32 0 2.51.45 3.44 1.35l2.58-2.58A9 9 0 0 0 9 0 9 9 0 0 0 .95 4.96l3 2.34C4.66 5.17 6.65 3.58 9 3.58z"
				fill="#EA4335"
			/>
		</svg>
	)
}
